// src/components/Circles.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";
import { colors, type, buttonPrimary, buttonSecondary, primaryHover } from "../styles/designTokens";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

const Circles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useNoIndex();

  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [circles, setCircles] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [events, setEvents] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submittingEvent, setSubmittingEvent] = useState(false);

  const [newCircleName, setNewCircleName] = useState("");
  // A wishlist idea "turned into a hangout" (see Wishlist.js) arrives via
  // router state and just pre-fills + opens this form - no separate API
  // call needed, the wishlist item and the event stay independent records.
  const prefill = location.state || {};
  const [showEventForm, setShowEventForm] = useState(!!prefill.prefillTitle);
  const [eventForm, setEventForm] = useState({
    title: prefill.prefillTitle || "",
    description: prefill.prefillDescription || "",
    eventDatetime: "",
    location: "",
    visibility: "private",
    circleId: "",
  });

  useEffect(() => {
    const validateAndLoad = async () => {
      const token = localStorage.getItem("memberstackToken");
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      let currentMember = null;
      try {
        currentMember = await memberstack.getCurrentMember();
      } catch (err) {
        console.error("Circles › getCurrentMember error:", err);
      }

      if (!currentMember || !currentMember.data) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      const hasValidStatus = (currentMember.data.planConnections || []).some((plan) => {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();
        return type === "subscription" && ["active", "trialing", "cancelled", "canceled"].includes(status);
      });

      setIsVerifyingAuth(false);

      if (!hasValidStatus) {
        navigate("/checkout");
        return;
      }

      await loadData(token);
      setLoading(false);
    };

    validateAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadData = async (token) => {
    try {
      const [circlesData, invitesData, eventsData] = await Promise.all([
        standardUserApi.getCircles(token),
        standardUserApi.getPendingCircleInvites(token),
        standardUserApi.getSelfRunEvents(token),
      ]);
      setCircles(circlesData);
      setPendingInvites(invitesData);
      setEvents(eventsData);
    } catch (err) {
      console.error("Circles › load error:", err);
      setErrorMessage("Couldn't load your circles right now. Please try again shortly.");
    }
  };

  const handleAcceptInvite = async (circleId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.acceptCircleInvite(token, circleId);
      await loadData(token);
    } catch (err) {
      console.error("Circles › accept invite error:", err);
      setErrorMessage("Couldn't accept that invite right now. Please try again shortly.");
    }
  };

  const handleDeclineInvite = async (circleId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.leaveCircle(token, circleId);
      await loadData(token);
    } catch (err) {
      console.error("Circles › decline invite error:", err);
      setErrorMessage("Couldn't decline that invite right now. Please try again shortly.");
    }
  };

  const handleLeaveCircle = async (circleId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.leaveCircle(token, circleId);
      await loadData(token);
    } catch (err) {
      console.error("Circles › leave error:", err);
      setErrorMessage("Couldn't leave that circle right now. Please try again shortly.");
    }
  };

  const handleCreateCircle = async (e) => {
    e.preventDefault();
    const name = newCircleName.trim();
    if (!name) return;
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.createCircle(token, { name, memberUserIds: [] });
      setNewCircleName("");
      setSuccessMessage("Circle created.");
      await loadData(token);
    } catch (err) {
      console.error("Circles › create error:", err);
      setErrorMessage("Couldn't create that circle right now. Please try again shortly.");
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.eventDatetime) return;
    const token = localStorage.getItem("memberstackToken");
    setSubmittingEvent(true);
    try {
      await standardUserApi.createSelfRunEvent(token, {
        title: eventForm.title.trim(),
        description: eventForm.description.trim() || null,
        eventDatetime: new Date(eventForm.eventDatetime).toISOString(),
        location: eventForm.location.trim() || null,
        visibility: eventForm.visibility,
        circleId: eventForm.circleId || null,
      });
      setEventForm({ title: "", description: "", eventDatetime: "", location: "", visibility: "private", circleId: "" });
      setShowEventForm(false);
      setSuccessMessage("Hangout created.");
      await loadData(token);
    } catch (err) {
      console.error("Circles › create event error:", err);
      setErrorMessage("Couldn't create that hangout right now. Please try again shortly.");
    } finally {
      setSubmittingEvent(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    setSuccessMessage("Link copied.");
  };

  if (isVerifyingAuth || loading) {
    return <LoadingState />;
  }

  if (showLoginModal) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <p style={{ color: "#374151", marginBottom: "16px" }}>Please log in to plan a hangout.</p>
        <Link to="/login" style={{ color: "#8B5FBF", fontWeight: "600" }}>Log in</Link>
      </div>
    );
  }

  return (
    <>
    <div style={{ background: colors.gradientTint, borderBottom: `1px solid ${colors.primaryTintBorder}` }}>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "44px 20px 32px", textAlign: "center" }}>
        <p style={type.eyebrow}>Beyond official gatherings</p>
        <h1 style={{ ...type.h1, marginTop: "10px", marginBottom: "12px" }}>Plan a Hangout</h1>
        <p style={{ ...type.body, maxWidth: "440px", margin: "0 auto 8px" }}>
          Keep the momentum going with people you've connected with. Private hangouts are just for
          your circle; public ones can be shared with anyone, even people who aren't on OkGather yet.
        </p>
        <p style={{ ...type.small, marginTop: "8px" }}>
          Separate from OkGather's own official gatherings, which still need an active subscription to book.
        </p>
      </div>
    </div>
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "32px 20px 40px" }}>

      {errorMessage && <p style={{ color: "#B91C1C", textAlign: "center", marginBottom: "16px" }}>{errorMessage}</p>}
      {successMessage && <p style={{ color: "#15803D", textAlign: "center", marginBottom: "16px" }}>{successMessage}</p>}

      {/* Circles */}
      <div style={{ marginBottom: "32px" }}>
        {pendingInvites.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <p style={{ color: "#B45309", fontWeight: "700", fontSize: "0.9rem", marginBottom: "8px" }}>Circle invites</p>
            {pendingInvites.map((inv) => (
              <div
                key={inv.id}
                style={{
                  border: "1.5px solid #F59E0B", background: "#FFFBEB", borderRadius: "12px", padding: "12px 18px",
                  marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap",
                }}
              >
                <span style={{ color: "#111827", fontWeight: "600" }}>
                  {inv.name} <span style={{ color: "#9CA3AF", fontWeight: "400" }}>· invited by {inv.invitedByFirstName}</span>
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => handleAcceptInvite(inv.id)}
                    style={{ backgroundColor: "#8B5FBF", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontWeight: "600", cursor: "pointer", fontSize: "0.85rem" }}
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleDeclineInvite(inv.id)}
                    style={{ backgroundColor: "transparent", color: "#9CA3AF", border: "1.5px solid #E5E7EB", borderRadius: "8px", padding: "6px 14px", fontWeight: "600", cursor: "pointer", fontSize: "0.85rem" }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <h5 style={{ color: "#111827", fontWeight: "700", marginBottom: "12px" }}>Your Circles</h5>
        {circles.length === 0 && (
          <p style={{ color: "#6B7280", marginBottom: "12px" }}>No circles yet. Create one below to get started.</p>
        )}
        {circles.map((c) => (
          <div key={c.id} style={{ border: "1.5px solid #E5E7EB", borderRadius: "12px", padding: "12px 18px", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#111827", fontWeight: "600" }}>{c.name}</span>
            <button
              onClick={() => handleLeaveCircle(c.id)}
              style={{ background: "none", border: "none", color: "#9CA3AF", cursor: "pointer", fontSize: "0.8rem" }}
            >
              Leave
            </button>
          </div>
        ))}
        <form onSubmit={handleCreateCircle} style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
          <input
            type="text"
            value={newCircleName}
            onChange={(e) => setNewCircleName(e.target.value)}
            placeholder="Circle name (e.g. 'Sunday football crew')"
            style={{ flex: 1, padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
          />
          <button
            type="submit"
            style={{ backgroundColor: "#8B5FBF", color: "#fff", border: "none", borderRadius: "10px", padding: "10px 20px", fontWeight: "700", cursor: "pointer" }}
          >
            Create
          </button>
        </form>
      </div>

      {/* Self-run events */}
      <div>
        <h5 style={{ color: "#111827", fontWeight: "700", marginBottom: "12px" }}>Your Hangouts</h5>
        {events.length === 0 && !showEventForm && (
          <p style={{ color: "#6B7280", marginBottom: "12px" }}>No hangouts yet.</p>
        )}
        {events.map((ev) => (
          <div key={ev.id} style={{ border: "1.5px solid #E5E7EB", borderRadius: "12px", padding: "14px 18px", marginBottom: "10px" }}>
            <div style={{ color: "#111827", fontWeight: "700" }}>{ev.title}</div>
            <div style={{ color: "#6B7280", fontSize: "0.9rem" }}>
              {new Date(ev.eventDatetime).toLocaleString()} {ev.location ? `· ${ev.location}` : ""}
            </div>
            <div style={{ color: "#9CA3AF", fontSize: "0.85rem", marginTop: "4px" }}>
              {ev.visibility === "public" ? "Public" : "Private"}
            </div>
            {ev.joinUrl && (
              <button
                onClick={() => copyLink(ev.joinUrl)}
                style={{
                  marginTop: "8px", backgroundColor: "transparent", color: "#8B5FBF", border: "1.5px solid #8B5FBF",
                  borderRadius: "8px", padding: "6px 14px", fontWeight: "600", cursor: "pointer", fontSize: "0.85rem",
                }}
              >
                Copy invite link
              </button>
            )}
          </div>
        ))}

        {!showEventForm ? (
          <button
            onClick={() => setShowEventForm(true)}
            style={{ ...buttonPrimary, marginTop: "12px" }}
            {...primaryHover}
          >
            + New Hangout
          </button>
        ) : (
          <form onSubmit={handleCreateEvent} style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <input
              type="text"
              placeholder="Title"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
            />
            <textarea
              placeholder="Description (optional)"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB", minHeight: "60px" }}
            />
            <label htmlFor="hangout-datetime" style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", fontWeight: "600", color: "#374151" }}>
              <span aria-hidden="true">📅</span> Date &amp; time (click to open calendar)
            </label>
            <input
              id="hangout-datetime"
              type="datetime-local"
              value={eventForm.eventDatetime}
              onChange={(e) => setEventForm({ ...eventForm, eventDatetime: e.target.value })}
              onClick={(e) => {
                if (typeof e.currentTarget.showPicker === "function") {
                  e.currentTarget.showPicker();
                }
              }}
              style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB", cursor: "pointer" }}
            />
            <input
              type="text"
              placeholder="Location (optional)"
              value={eventForm.location}
              onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
            />
            <select
              value={eventForm.circleId}
              onChange={(e) => setEventForm({ ...eventForm, circleId: e.target.value })}
              style={{ padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
            >
              <option value="">No circle</option>
              {circles.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "#374151" }}>
                <input
                  type="radio"
                  checked={eventForm.visibility === "private"}
                  onChange={() => setEventForm({ ...eventForm, visibility: "private" })}
                />
                Private (circle only)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "#374151" }}>
                <input
                  type="radio"
                  checked={eventForm.visibility === "public"}
                  onChange={() => setEventForm({ ...eventForm, visibility: "public" })}
                />
                Public (shareable link, no account needed to RSVP)
              </label>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="submit"
                disabled={submittingEvent}
                style={{
                  ...buttonPrimary, padding: "11px 22px", fontSize: "0.9rem",
                  cursor: submittingEvent ? "default" : "pointer", opacity: submittingEvent ? 0.7 : 1,
                }}
                onMouseEnter={submittingEvent ? undefined : primaryHover.onMouseEnter}
                onMouseLeave={submittingEvent ? undefined : primaryHover.onMouseLeave}
              >
                {submittingEvent ? "Creating..." : "Create hangout"}
              </button>
              <button
                type="button"
                onClick={() => setShowEventForm(false)}
                disabled={submittingEvent}
                style={{
                  ...buttonSecondary, padding: "10px 22px", fontSize: "0.9rem", borderColor: colors.border, color: colors.muted,
                  cursor: submittingEvent ? "default" : "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    </>
  );
};

export default Circles;
